import { Globe2 } from "lucide-react";
import PlatformTile from "./PlatformTile";
import LogoSlot from "./LogoSlot";
import SectionKicker from "./SectionKicker";
import useScrollFade from "../hooks/useScrollFade";
import { ingroove, mgozi, goal } from "../data/content";
import { INGROOVE_LOGO_SRC, INGROOVE_BANNER_SRC } from "../constants/brand";

function RevealTile({ children }) {
  const [ref, visible] = useScrollFade();
  return (
    <div ref={ref} className={`reveal ${visible ? "is-visible" : ""}`}>
      {children}
    </div>
  );
}

export default function ProjectsSection() {
  return (
    <section id="platforms" className="px-6 py-24 md:py-32">
      <div className="mx-auto max-w-site space-y-6">
          <SectionKicker index="01" label="Platforms" />
          {/* Ingroove */}
          <RevealTile>
            <PlatformTile
              variant="black"
              className="relative overflow-hidden px-6 pb-14 pt-14 text-center transition-transform duration-300 hover:-translate-y-1 sm:px-10 md:pb-20 md:pt-16"
            >
              {/* Decorative glow + grid texture */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-0 h-80 w-80 -translate-x-1/2 -translate-y-1/3 rounded-full bg-omnyx-cyan/20 blur-3xl"
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-16 bottom-0 h-72 w-72 rounded-full bg-omnyx-teal/25 blur-3xl"
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    "radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)",
                  backgroundSize: "24px 24px",
                }}
              />

              <div className="relative">
                <LogoSlot
                  src={INGROOVE_LOGO_SRC}
                  alt="Ingroove"
                  className="mx-auto mb-6 h-12 w-12"
                  placeholderClassName="h-12 w-12 text-white"
                />
                <p className="mb-4 text-sm font-semibold uppercase tracking-wide text-omnyx-cyan">
                  {ingroove.label}
                </p>
                <h2 className="font-heading text-5xl font-extrabold tracking-tightest text-white md:text-[72px]">
                  {ingroove.title}
                </h2>
                <p className="mt-3 text-lg font-semibold text-white/80 md:text-xl">
                  {ingroove.tagline}
                </p>
                <p className="mx-auto mt-5 max-w-2xl text-white/70">
                  {ingroove.description}
                </p>

                <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                  <a
                    href={ingroove.primaryCta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-omnyx-cyan px-8 py-3 font-semibold text-site-tile-black shadow-glow-cyan transition-colors hover:bg-omnyx-cyan-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-omnyx-cyan btn-sheen"
                  >
                    {ingroove.primaryCta.label}
                  </a>
                  <a
                    href={ingroove.secondaryCta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[44px] items-center justify-center rounded-full px-4 py-3 font-semibold text-white transition-colors hover:text-omnyx-cyan focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-omnyx-cyan"
                  >
                    {ingroove.secondaryCta.label}
                  </a>
                </div>

                {/* Screenshot shown in a contained device frame, not full-bleed */}
                <div className="mx-auto mt-12 max-w-3xl rotate-1 overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-2xl transition-transform duration-300 hover:rotate-0">
                  <div className="flex items-center gap-1.5 border-b border-white/10 bg-white/5 px-4 py-3">
                    <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                    <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                    <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  </div>
                  <img
                    src={INGROOVE_BANNER_SRC}
                    alt="Ingroove app preview showing event listings and ticket booking"
                    loading="lazy"
                    decoding="async"
                    className="block h-auto w-full object-cover object-top"
                  />
                </div>
              </div>
            </PlatformTile>
          </RevealTile>

          {/* Mgozi + Goal */}
          <div className="grid gap-6 md:grid-cols-2">
            <RevealTile>
              <PlatformTile
                variant="surface"
                className="relative h-full overflow-hidden p-8 transition-transform duration-300 hover:-translate-y-1 md:p-10"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-site-pink/10 blur-3xl"
                />
                <div className="relative">
                  <span className="inline-block rounded-full bg-site-pink/15 px-3 py-1 text-xs font-semibold text-site-pink">
                    {mgozi.badge}
                  </span>
                  <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tightest text-site-ink md:text-4xl">
                    {mgozi.title}
                  </h2>
                  <p className="mt-2 font-semibold text-site-ink/80">
                    {mgozi.tagline}
                  </p>
                  <p className="mt-4 text-site-muted">{mgozi.description}</p>
                </div>
              </PlatformTile>
            </RevealTile>

            <RevealTile>
              <PlatformTile
                variant="brand"
                className="relative h-full overflow-hidden p-8 transition-transform duration-300 hover:-translate-y-1 md:p-10"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -bottom-12 -left-10 h-56 w-56 rounded-full bg-white/10 blur-3xl"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 opacity-20"
                  style={{
                    backgroundImage:
                      "radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)",
                    backgroundSize: "20px 20px",
                  }}
                />
                <div className="relative">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                    <Globe2 className="h-3.5 w-3.5" />
                    {goal.label}
                  </span>
                  <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tightest text-white md:text-4xl">
                    {goal.title}
                  </h2>
                  <p className="mt-4 text-white/80">{goal.description}</p>
                </div>
              </PlatformTile>
            </RevealTile>
          </div>
        </div>
      </section>
  );
}

